import { ErrorDetailMap } from '@paywithextend/components/dist/error.utils'
import { AuthUser } from 'aws-amplify/auth'
import { Location } from 'history'
import { produce } from 'immer'
import moment from 'moment'
import {
  GET_CURRENT_DEVICE_KEY_INIT,
  GET_CURRENT_DEVICE_KEY_SUCCESS,
  GetCurrentDeviceKeyActions,
} from 'src/app/ManageTrustedDevices/redux/manageTrustedDevices.actions'
import { defaultPerSessionValues, PerSessionValuesType } from 'src/cookies'
import { ExtendLocales, UserStatus } from 'src/enums'
import { homeUrl } from 'src/navigation/urls'
import {
  ABANDON_MFA_CHALLENGE,
  CLEAR_AUTH_FLOW_TYPE,
  CONFIRM_COGNITO_SIGNIN_SUCCESS,
  CONFIRM_CSP_COGNITO_SIGNIN_SUCCESS,
  ConfirmCognitoSigninActions,
  CSP_AUTHED_SUCCESS,
  DELETE_COGNITO_SIGNOUT_SUCCESS,
  DELETE_CSP_EMULATED_SESSIONS_FAILURE,
  DELETE_CSP_EMULATED_SESSIONS_SUCCESS,
  DELETE_SIGNOUT_SUCCESS,
  DeleteCognitoSignoutActions,
  DeleteCspEmulatedSessionsActions,
  DeleteSignoutActions,
  GET_COGNITO_SIGNIN_THEN_PUSH_SUCCESS,
  GET_CSP_COGNITO_SIGNIN_THEN_PUSH_SUCCESS,
  GET_CSP_ME_FAILURE,
  GET_CSP_ME_SUCCESS,
  GET_HEARTBEAT_FAILURE,
  GET_HEARTBEAT_SUCCESS,
  GET_ME_FAILURE,
  GET_ME_SUCCESS,
  GetCognitoSigninThenPushActions,
  GetCspMeFailureAction,
  GetCspMeSuccessAction,
  GetHeartbeatFailureAction,
  GetHeartbeatSuccessAction,
  GetMeFailureAction,
  GetMeSuccessAction,
  INIT_MFA_CHALLENGE,
  POPULATE_USER_INFO_LEGACY,
  POST_AMPLIFY_SIGNIN_FAILURE,
  POST_AMPLIFY_SIGNIN_INIT,
  POST_AMPLIFY_SIGNIN_SUCCESS,
  POST_AUTH_FLOW_TYPE_FAILURE,
  POST_AUTH_FLOW_TYPE_INIT,
  POST_AUTH_FLOW_TYPE_SUCCESS,
  POST_COGNITO_LOOKUP_FAILURE,
  POST_COGNITO_RENEW_AUTH_FAILURE,
  POST_COGNITO_RENEW_AUTH_INIT,
  POST_COGNITO_RENEW_AUTH_SUCCESS,
  POST_COGNITO_RESET_PASSWORD_CONFIRM_FAILURE,
  POST_COGNITO_RESET_PASSWORD_CONFIRM_INIT,
  POST_COGNITO_RESET_PASSWORD_CONFIRM_SUCCESS,
  POST_CSP_EMULATED_SESSIONS_LIVE_FAILURE,
  POST_CSP_EMULATED_SESSIONS_LIVE_INIT,
  POST_CSP_EMULATED_SESSIONS_LIVE_SUCCESS,
  POST_CSP_EMULATED_SESSIONS_OFFLINE_FAILURE,
  POST_CSP_EMULATED_SESSIONS_OFFLINE_INIT,
  POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS,
  POST_CSP_LOGIN_FAILURE,
  POST_CSP_LOGIN_INIT,
  POST_CSP_LOGIN_ISSUER_INFO_FAILURE,
  POST_CSP_LOGIN_ISSUER_INFO_INIT,
  POST_CSP_LOGIN_ISSUER_INFO_SUCCESS,
  POST_CSP_LOGIN_SUCCESS,
  POST_LOOKUP_FAILURE,
  POST_LOOKUP_INIT,
  POST_PHONE_VERIFICATION_FAILURE,
  POST_PHONE_VERIFICATION_INIT,
  POST_PHONE_VERIFICATION_SUCCESS,
  POST_RENEW_AUTH_FAILURE,
  POST_RENEW_AUTH_INIT,
  POST_RENEW_AUTH_SUCCESS,
  POST_RESEND_PHONE_VERIFICATION_CODE_SUCCESS,
  POST_SEND_PHONE_VERIFICATION_CODE_FAILURE,
  POST_SEND_PHONE_VERIFICATION_CODE_INIT,
  POST_SEND_PHONE_VERIFICATION_CODE_SUCCESS,
  POST_SIGNIN_FAILURE,
  POST_SIGNIN_INIT,
  POST_SIGNIN_SUCCESS,
  POST_USER_SUCCESS,
  POST_USER_VERIFICATION_SUCCESS,
  PostAmplifySigninActions,
  PostAuthFlowTypeActions,
  PostCognitoRenewAuthActions,
  PostCognitoResetPasswordConfirmActions,
  PostCognitoResetPasswordInitActions,
  PostCspEmulatedSessionsLiveActions,
  PostCspEmulatedSessionsOfflineActions,
  PostCspLoginActions,
  PostCspLoginIssuerInfoActions,
  PostLookupActions,
  PostPhoneVerificationActions,
  PostRenewAuthActions,
  PostSendPhoneVerificationCodeActions,
  PostSigninActions,
  PostUserSuccessAction,
  PostUserVerificationSuccessAction,
  PUT_CSP_EMULATED_SESSIONS_REFRESH_SUCCESS,
  PUT_CSP_EMULATED_SESSIONS_VERIFY_FAILURE,
  PUT_CSP_EMULATED_SESSIONS_VERIFY_INIT,
  PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS,
  PutCspEmulatedSessionsRefreshActions,
  PutCspEmulatedSessionsVerifyActions,
  RESET_MFA_CODE_STATE_ACTION,
  RESET_VERIFICATION_ERRORS,
  ResetVerificationErrorsAction,
  SET_EMAIL,
  SET_PER_SESSION_VALUES,
  SET_UNAUTHED_LOCALE,
  SET_USER_STATUS,
  SetEmailAction,
  SetPerSessionValues,
  SetUnauthedLocaleAction,
  SetUserStatusAction,
  UPDATE_ACCEPT_ORG_INVITE,
  UPDATE_ORG_INVITE_ERROR,
  UpdateAcceptOrgInviteAction,
  UpdateOrgInviteErrorAction,
} from 'src/redux/actions/auth.actions'
import {
  DELETE_USER_SUCCESS,
  DeleteUserSuccessAction,
} from 'src/redux/actions/data/users.actions'
import { parseBackendErrors } from 'src/utils/error.utils'

export interface AuthState {
  email: string
  userStatus?: UserStatus
  authedRedirectLocation: Location
  didSessionExpireAutomatically: boolean
  isAuthenticated: boolean
  jwt?: string
  refreshToken?: string
  userId?: string
  isRenewingAuth: boolean
  renewAuthTopLevelError?: string
  renewAuthErrorDetailMap?: ErrorDetailMap<string>
  isCspAuthenticated: boolean
  userSessionTokenId?: string
  cspJwt?: string
  cspRefreshToken?: string
  cspUserId?: string
  cspIssuerIds?: string[]
  startTimeStamp?: moment.Moment
  fetchingAuthType: boolean
  fetchAuthTypeTopLevelError?: string
  fetchAuthTypeErrorDetailMap?: ErrorDetailMap<string>
  authType?: string
  resettingPassword: boolean
  resetPasswordTopLevelError?: string
  resetPasswordErrorDetailMap?: ErrorDetailMap<string>
  mfaChallengeInitiated: boolean
  mfaSent: boolean
  mfaDestination?: string
  mfaDeliveryMedium?: string
  cognitoUser?: AuthUser
  fetchingCurrentDeviceKey: boolean
  deviceKey?: string
  perSessionValues?: PerSessionValuesType
  hideWelcomeBackBanner?: boolean
  acceptOrgInvite?: boolean
  orgInviteError?: boolean
  unauthedLocale?: ExtendLocales
  verifyPhoneTopLevelError?: string
  resendVerificationTopLevelError?: string
  verifyPhoneVerificationLoading?: boolean
  sendPhoneVerificationLoading?: boolean
  getPhoneNumberResponseValue?: string
  userEmailVerificationStatus?: string
  verificationPhoneResent: boolean
}

export const initialState: AuthState = {
  email: '',
  isAuthenticated: false,
  isCspAuthenticated: false,
  isRenewingAuth: false,
  authedRedirectLocation: {
    pathname: homeUrl(),
    search: '',
    state: undefined,
    hash: '',
  },
  didSessionExpireAutomatically: false,
  resettingPassword: false,
  fetchingAuthType: false,
  mfaChallengeInitiated: false,
  mfaSent: false,
  fetchingCurrentDeviceKey: false,
  verifyPhoneVerificationLoading: false,
  sendPhoneVerificationLoading: false,
  verificationPhoneResent: false,
  getPhoneNumberResponseValue: '',
  userEmailVerificationStatus: '',
}

export type Actions =
  | SetUserStatusAction
  | SetEmailAction
  | GetMeSuccessAction
  | GetMeFailureAction
  | GetCspMeSuccessAction
  | GetCspMeFailureAction
  | PostLookupActions
  | PostSigninActions
  | PostCspLoginActions
  | PostCspEmulatedSessionsOfflineActions
  | PostCspEmulatedSessionsLiveActions
  | PutCspEmulatedSessionsVerifyActions
  | PutCspEmulatedSessionsRefreshActions
  | DeleteSignoutActions
  | DeleteCspEmulatedSessionsActions
  | PostUserVerificationSuccessAction
  | PostUserSuccessAction
  | DeleteUserSuccessAction
  | GetHeartbeatSuccessAction
  | GetHeartbeatFailureAction
  | PostRenewAuthActions
  | PostCognitoRenewAuthActions
  | PostAuthFlowTypeActions
  | PostCognitoResetPasswordInitActions
  | PostCognitoResetPasswordConfirmActions
  | PostAmplifySigninActions
  | ConfirmCognitoSigninActions
  | GetCognitoSigninThenPushActions
  | GetCurrentDeviceKeyActions
  | PostCspLoginIssuerInfoActions
  | DeleteCognitoSignoutActions
  | SetPerSessionValues
  | UpdateAcceptOrgInviteAction
  | UpdateOrgInviteErrorAction
  | SetUnauthedLocaleAction
  | PostSendPhoneVerificationCodeActions
  | PostPhoneVerificationActions
  | ResetVerificationErrorsAction

const AuthReducer = (
  state: AuthState = initialState,
  action: Actions,
): AuthState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case SET_USER_STATUS: {
        draft.userStatus = action.payload.status
        return draft
      }

      case SET_EMAIL: {
        draft.email = action.payload.email
        return draft
      }

      case GET_ME_FAILURE: {
        draft.userId = undefined
        draft.isAuthenticated = false
        draft.jwt = undefined
        draft.refreshToken = undefined
        draft.userSessionTokenId = undefined
        return draft
      }
      case GET_ME_SUCCESS: {
        draft.isAuthenticated = true
        draft.userId = action.payload.id
        return draft
      }

      case GET_CSP_ME_FAILURE: {
        draft.cspUserId = undefined
        draft.isCspAuthenticated = false
        draft.cspJwt = undefined
        draft.cspRefreshToken = undefined
        return draft
      }
      case GET_CSP_ME_SUCCESS: {
        draft.isCspAuthenticated = true
        draft.cspUserId = action.payload.user.id
        draft.cspIssuerIds = action.payload.issuers.map((iss) => iss.id)
        return draft
      }

      case POST_LOOKUP_INIT: {
        draft.fetchAuthTypeTopLevelError = undefined
        draft.fetchAuthTypeErrorDetailMap = undefined
        return draft
      }
      case POST_COGNITO_LOOKUP_FAILURE:
      case POST_LOOKUP_FAILURE: {
        return draft
      }

      case DELETE_COGNITO_SIGNOUT_SUCCESS:
      case DELETE_SIGNOUT_SUCCESS: {
        draft.userStatus = undefined
        draft.email = ''
        draft.authedRedirectLocation = action.payload.authedRedirectLocation
        draft.didSessionExpireAutomatically =
          action.payload.didSessionExpireAutomatically
        draft.userId = undefined
        draft.isAuthenticated = false
        draft.jwt = undefined
        draft.refreshToken = undefined
        draft.userSessionTokenId = undefined
        if (action.payload.isCsp) {
          draft.cspUserId = undefined
          draft.isCspAuthenticated = false
          draft.cspJwt = undefined
          draft.cspRefreshToken = undefined
        }
        draft.mfaChallengeInitiated = false
        draft.authType = undefined
        draft.startTimeStamp = undefined
        draft.perSessionValues = undefined
        draft.hideWelcomeBackBanner = undefined
        draft.orgInviteError = false
        return draft
      }

      case DELETE_CSP_EMULATED_SESSIONS_SUCCESS: {
        draft.userStatus = undefined
        draft.email = ''
        // draft.authedRedirectLocation = action.payload.authedRedirectLocation
        draft.didSessionExpireAutomatically =
          action.payload.didSessionExpireAutomatically
        draft.userId = undefined
        draft.isAuthenticated = false
        draft.jwt = undefined
        draft.refreshToken = undefined
        draft.userSessionTokenId = undefined
        draft.startTimeStamp = undefined
        draft.orgInviteError = false
        return draft
      }

      case DELETE_CSP_EMULATED_SESSIONS_FAILURE: {
        draft.userStatus = undefined
        draft.email = ''
        draft.didSessionExpireAutomatically = false
        draft.userId = undefined
        draft.isAuthenticated = false
        draft.jwt = undefined
        draft.refreshToken = undefined
        draft.userSessionTokenId = undefined
        draft.startTimeStamp = undefined
        draft.orgInviteError = false
        return draft
      }

      case DELETE_USER_SUCCESS: {
        draft.isAuthenticated = false
        draft.jwt = undefined
        draft.refreshToken = undefined
        draft.userId = undefined
        draft.userStatus = undefined
        draft.email = ''
        draft.authedRedirectLocation = action.payload.authedRedirectLocation
        draft.userSessionTokenId = undefined
        draft.orgInviteError = false
        return draft
      }

      case GET_HEARTBEAT_SUCCESS: {
        draft.authedRedirectLocation = action.payload.authedRedirectLocation
        return draft
      }

      case GET_HEARTBEAT_FAILURE: {
        // TODO: is heartbeat still important to handle separately in case of non 401?
        draft.userStatus = undefined
        draft.email = ''
        draft.authedRedirectLocation = action.payload.authedRedirectLocation
        draft.didSessionExpireAutomatically = true
        if (action.payload.isCsp) {
          draft.cspUserId = undefined
          draft.isCspAuthenticated = false
          draft.cspJwt = undefined
          draft.cspRefreshToken = undefined
          draft.orgInviteError = false
        } else {
          draft.userId = undefined
          draft.isAuthenticated = false
          draft.jwt = undefined
          draft.refreshToken = undefined
          draft.userSessionTokenId = undefined
          draft.orgInviteError = false
        }
        return draft
      }

      case POST_SIGNIN_INIT: {
        draft.jwt = undefined
        return draft
      }

      case POST_AMPLIFY_SIGNIN_INIT: {
        draft.jwt = undefined
        return draft
      }

      case GET_COGNITO_SIGNIN_THEN_PUSH_SUCCESS: {
        draft.isAuthenticated = true
        draft.userId = action.payload.id
        draft.didSessionExpireAutomatically = false
        return draft
      }

      case GET_CSP_COGNITO_SIGNIN_THEN_PUSH_SUCCESS: {
        draft.isCspAuthenticated = true
        draft.cspUserId = action.payload.id
        draft.didSessionExpireAutomatically = false
        return draft
      }

      case POST_AMPLIFY_SIGNIN_SUCCESS:
      case CONFIRM_COGNITO_SIGNIN_SUCCESS: {
        // there is currently a lot of tight coupling between our front-end sign-in flows and our auth state flows
        // i.e. POST_AMPLIFY_SIGNIN_SUCCESS has to be dispatched to set `isSigningIn` to false so that users can complete
        // the mfa code entry form
        // therefore, it's only actually a successful sign in case when the jwt is set, but we can't gate it earlier
        // due to the sign in reducers reliance on this action being dispatched
        if (action.payload.jwt !== undefined) {
          draft.jwt = action.payload.jwt
          // refreshTokens are handled internally by Amplify v6, but a non-undefined value is required to continue
          // support for legacy Auth0 flows
          draft.refreshToken = 'HANDLED_INTERNALLY_BY_AMPLIFY_V6'
          draft.perSessionValues = defaultPerSessionValues // reset per session variables on login
          draft.mfaChallengeInitiated = false
        }
        return draft
      }

      case CONFIRM_CSP_COGNITO_SIGNIN_SUCCESS: {
        draft.cspJwt = action.payload.jwt
        // refreshTokens are handled internally by Amplify v6, but a non-undefined value is required to continue
        // support for legacy Auth0 flows
        draft.cspRefreshToken = 'HANDLED_INTERNALLY_BY_AMPLIFY_V6'
        return draft
      }

      case POPULATE_USER_INFO_LEGACY: {
        draft.isAuthenticated = true
        draft.jwt = action.payload.jwt
        draft.userId = action.payload.userId
        // refreshTokens are handled internally by Amplify v6, but a non-undefined value is required to continue
        // support for legacy Auth0 flows
        draft.refreshToken = 'HANDLED_INTERNALLY_BY_AMPLIFY_V6'
        draft.didSessionExpireAutomatically = false
        draft.perSessionValues = defaultPerSessionValues // reset per session variables on login
        draft.mfaChallengeInitiated = false
        return draft
      }

      case GET_CURRENT_DEVICE_KEY_INIT: {
        draft.fetchingCurrentDeviceKey = true
        return draft
      }

      case GET_CURRENT_DEVICE_KEY_SUCCESS: {
        draft.deviceKey = action.payload
        draft.fetchingCurrentDeviceKey = false
        return draft
      }

      case POST_SIGNIN_SUCCESS: {
        draft.isAuthenticated = true
        draft.jwt = action.payload.token
        draft.refreshToken = action.payload.refreshToken
        draft.userId = action.payload.user?.id
        draft.didSessionExpireAutomatically = false
        draft.perSessionValues = defaultPerSessionValues // reset per session variables on login
        draft.acceptOrgInvite = action.payload?.acceptOrgInvite
        return draft
      }

      case POST_SIGNIN_FAILURE: {
        return draft
      }

      case UPDATE_ACCEPT_ORG_INVITE: {
        draft.acceptOrgInvite = action.payload
        return draft
      }

      case UPDATE_ORG_INVITE_ERROR: {
        draft.orgInviteError = action.payload
        return draft
      }

      case POST_CSP_LOGIN_INIT: {
        return draft
      }
      case POST_CSP_LOGIN_SUCCESS: {
        draft.isCspAuthenticated = true
        draft.cspJwt = action.payload.token
        draft.cspRefreshToken = action.payload.refreshToken
        draft.cspUserId = action.payload.user.id
        draft.cspIssuerIds = action.payload.issuers.map((iss) => iss.id)
        return draft
      }
      case POST_CSP_LOGIN_FAILURE: {
        return draft
      }

      case POST_CSP_LOGIN_ISSUER_INFO_INIT: {
        return draft
      }
      case POST_CSP_LOGIN_ISSUER_INFO_SUCCESS: {
        draft.cspUserId = action.payload.user.id
        draft.cspIssuerIds = action.payload.issuers.map((iss) => iss.id)
        return draft
      }
      case POST_CSP_LOGIN_ISSUER_INFO_FAILURE: {
        return draft
      }

      case CSP_AUTHED_SUCCESS: {
        draft.isCspAuthenticated = true
        return draft
      }

      case POST_CSP_EMULATED_SESSIONS_OFFLINE_INIT: {
        return draft
      }
      case POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS: {
        draft.isAuthenticated = true
        draft.jwt = action.payload.token.auth
        draft.userId = action.payload.token.resource.emulatedUser.id
        draft.didSessionExpireAutomatically = false
        draft.userSessionTokenId = action.payload.token.id
        draft.startTimeStamp = action.startTimeStamp
        return draft
      }
      case POST_CSP_EMULATED_SESSIONS_OFFLINE_FAILURE: {
        return draft
      }

      case POST_CSP_EMULATED_SESSIONS_LIVE_INIT: {
        return draft
      }
      case POST_CSP_EMULATED_SESSIONS_LIVE_SUCCESS: {
        return draft
      }
      case POST_CSP_EMULATED_SESSIONS_LIVE_FAILURE: {
        return draft
      }

      case PUT_CSP_EMULATED_SESSIONS_VERIFY_INIT: {
        return draft
      }
      case PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS: {
        draft.isAuthenticated = true
        draft.jwt = action.payload.token.auth
        draft.userId = action.payload.token.resource.emulatedUser.id
        draft.didSessionExpireAutomatically = false
        draft.userSessionTokenId = action.payload.token.id
        draft.startTimeStamp = action.startTimeStamp
        return draft
      }
      case PUT_CSP_EMULATED_SESSIONS_VERIFY_FAILURE: {
        return draft
      }

      case PUT_CSP_EMULATED_SESSIONS_REFRESH_SUCCESS: {
        draft.isAuthenticated = true
        draft.jwt = action.payload.token.auth
        draft.userId = action.payload.token.resource.emulatedUser.id
        draft.didSessionExpireAutomatically = false
        draft.userSessionTokenId = action.payload.token.id
        return draft
      }

      case POST_COGNITO_RENEW_AUTH_INIT:
      case POST_RENEW_AUTH_INIT: {
        draft.isRenewingAuth = true
        return draft
      }

      case POST_COGNITO_RENEW_AUTH_SUCCESS:
      case POST_RENEW_AUTH_SUCCESS: {
        draft.isRenewingAuth = false
        if (action.payload.isCsp) {
          draft.isCspAuthenticated = true
          draft.cspJwt = action.payload.token
          if (action.payload.refreshToken !== undefined) {
            draft.cspRefreshToken = action.payload.refreshToken
          }
        } else {
          draft.isAuthenticated = true
          draft.jwt = action.payload.token
          if (action.payload.refreshToken !== undefined) {
            draft.refreshToken = action.payload.refreshToken
          }
        }
        return draft
      }

      case POST_RENEW_AUTH_FAILURE: {
        // handled by UNAUTHORIZED_REQUEST
        draft.isRenewingAuth = false
        const errors = parseBackendErrors(action.payload)
        draft.renewAuthTopLevelError = errors.topLevelError
        draft.renewAuthErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case POST_COGNITO_RENEW_AUTH_FAILURE: {
        draft.isRenewingAuth = false
        draft.renewAuthTopLevelError = action.payload
        return draft
      }

      case POST_USER_VERIFICATION_SUCCESS: {
        draft.userStatus = UserStatus.Verified
        draft.userEmailVerificationStatus = action.payload.msg
        draft.hideWelcomeBackBanner = true
        return draft
      }

      case POST_USER_SUCCESS: {
        draft.userStatus = UserStatus.NotVerified
        return draft
      }

      case POST_AUTH_FLOW_TYPE_INIT: {
        draft.fetchingAuthType = true
        return draft
      }

      case POST_AUTH_FLOW_TYPE_SUCCESS: {
        draft.fetchingAuthType = false
        draft.authType = action.payload.type
        return draft
      }

      case CLEAR_AUTH_FLOW_TYPE: {
        draft.authType = undefined
        return draft
      }

      case POST_AUTH_FLOW_TYPE_FAILURE: {
        draft.fetchingAuthType = false
        const errors = parseBackendErrors(action.payload)
        draft.fetchAuthTypeTopLevelError = errors.topLevelError
        draft.fetchAuthTypeErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case POST_COGNITO_RESET_PASSWORD_CONFIRM_INIT: {
        draft.resettingPassword = true
        draft.resetPasswordTopLevelError = undefined
        draft.resetPasswordErrorDetailMap = undefined
        return draft
      }

      case POST_COGNITO_RESET_PASSWORD_CONFIRM_SUCCESS: {
        draft.resettingPassword = false
        draft.resetPasswordTopLevelError = undefined
        draft.resetPasswordErrorDetailMap = undefined
        return draft
      }

      case POST_COGNITO_RESET_PASSWORD_CONFIRM_FAILURE: {
        draft.resettingPassword = false
        const errors = parseBackendErrors(action.payload)
        draft.resetPasswordTopLevelError = errors.topLevelError
        draft.resetPasswordErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case INIT_MFA_CHALLENGE: {
        draft.mfaChallengeInitiated = true
        draft.mfaSent = true
        draft.mfaDestination = action.payload?.destination
        draft.mfaDeliveryMedium = action.payload?.deliveryMedium
        return draft
      }

      case RESET_MFA_CODE_STATE_ACTION:
      case ABANDON_MFA_CHALLENGE: {
        draft.mfaChallengeInitiated = false
        draft.mfaSent = false
        draft.mfaDestination = undefined
        draft.mfaDeliveryMedium = undefined
        return draft
      }

      case POST_AMPLIFY_SIGNIN_FAILURE: {
        draft.userStatus = undefined
        return draft
      }

      case SET_PER_SESSION_VALUES: {
        // let incoming partial perSessionValues override existing ones
        draft.perSessionValues = draft.perSessionValues
          ? {
              ...draft.perSessionValues,
              ...action.payload.perSessionValues,
            }
          : undefined
        return draft
      }

      case SET_UNAUTHED_LOCALE: {
        draft.unauthedLocale = action.payload
        return draft
      }

      case POST_PHONE_VERIFICATION_INIT: {
        draft.verifyPhoneVerificationLoading = true
        draft.verifyPhoneTopLevelError = undefined
        return draft
      }

      case POST_PHONE_VERIFICATION_SUCCESS: {
        draft.verifyPhoneVerificationLoading = false
        draft.verifyPhoneTopLevelError = undefined
        return draft
      }

      case POST_PHONE_VERIFICATION_FAILURE: {
        draft.verifyPhoneVerificationLoading = false
        const errors = parseBackendErrors(action.payload)
        draft.verifyPhoneTopLevelError = errors.topLevelError
        return draft
      }

      case POST_SEND_PHONE_VERIFICATION_CODE_INIT: {
        draft.verificationPhoneResent = false
        draft.sendPhoneVerificationLoading = true
        draft.resendVerificationTopLevelError = undefined
        return draft
      }

      case POST_RESEND_PHONE_VERIFICATION_CODE_SUCCESS: {
        draft.verificationPhoneResent = true
        draft.sendPhoneVerificationLoading = false
        draft.getPhoneNumberResponseValue = action.payload.msg
        draft.resendVerificationTopLevelError = undefined
        return draft
      }

      case POST_SEND_PHONE_VERIFICATION_CODE_SUCCESS: {
        draft.getPhoneNumberResponseValue = action.payload.msg
        draft.sendPhoneVerificationLoading = false
        draft.resendVerificationTopLevelError = undefined
        return draft
      }

      case POST_SEND_PHONE_VERIFICATION_CODE_FAILURE: {
        draft.sendPhoneVerificationLoading = false
        const errors = parseBackendErrors(action.payload)
        draft.resendVerificationTopLevelError = errors.topLevelError
        return draft
      }

      case RESET_VERIFICATION_ERRORS: {
        draft.resendVerificationTopLevelError = undefined
        draft.verifyPhoneTopLevelError = undefined
        return draft
      }
    }
  })
}

export default AuthReducer
